/**
 * Projects Zod schemas — shared FE ↔ BE.
 *
 * Per DESIGN.md §5.2 (endpoints 23–32) + ARCHITECTURE.md §4.3 +
 * REQUIREMENTS.md FR-PROJ-001..006.
 *
 * DB source of truth: apps/api/src/infrastructure/db/schema/projects.ts
 *
 * A-022 (RESOLVED 2026-05-23): A `reference` field (max 100) was missing from
 * the initial Drizzle schema and migration. The Product Owner confirmed it is
 * needed for job/quote numbering. Added via migration 0003_add_project_reference.sql
 * and the Drizzle schema update. Search (T-M2-003) uses ILIKE on name + reference.
 */
import { z } from 'zod';
import { UNIT_CODES } from './tenants';

/* ─── Role codes ─────────────────────────────────────────────────── */

/**
 * Role codes that can appear on a project share.
 * SYSTEM is excluded — it is not a human-assignable role.
 */
export const SHAREABLE_ROLE_CODES = [
  'OWNER',
  'ADMIN',
  'ESTIMATOR',
  'PRODUCTION',
  'SALES',
  'VIEWER',
] as const;
export type ShareableRoleCode = (typeof SHAREABLE_ROLE_CODES)[number];

/* ─── Create / Update ────────────────────────────────────────────── */

/**
 * Body for `POST /api/v1/projects`.
 *
 * `name` is the only required field; all project-level override defaults
 * are optional (the tenant's defaults apply when absent).
 */
export const CreateProjectSchema = z.object({
  /** Project display name — required. Max 200 chars. */
  name: z
    .string()
    .trim()
    .min(1, 'Project name is required')
    .max(200, 'Name too long (max 200)'),

  /**
   * Optional job / quote reference code (e.g. "JOB-2024-001").
   * Used for fabricators' own job-numbering systems. Max 100 chars.
   * Added via migration 0003_add_project_reference.sql (A-022).
   */
  reference: z.string().trim().max(100, 'Reference too long (max 100)').optional(),

  /** Optional client / company name. Max 200 chars. */
  clientName: z.string().trim().max(200, 'Client name too long (max 200)').optional(),

  /** Optional client contact (email, phone, or address). Max 200 chars. */
  clientContact: z
    .string()
    .trim()
    .max(200, 'Client contact too long (max 200)')
    .optional(),

  /**
   * Project-level kerf override (mm). Overrides the tenant default.
   * Range: 1–20 mm.
   */
  defaultKerfMm: z
    .number()
    .int('Must be a whole number')
    .min(1, 'Min 1mm')
    .max(20, 'Max 20mm')
    .optional(),

  /**
   * Project-level stock-bar length override (mm).
   * Overrides the tenant default; may itself be overridden per line item.
   * Range: 1000–12000 mm.
   */
  defaultStockBarMm: z
    .number()
    .int('Must be a whole number')
    .min(1000, 'Min 1000mm')
    .max(12000, 'Max 12000mm')
    .optional(),

  /**
   * Project-level offcut threshold override (mm).
   * Remainders >= this value become reusable offcuts; below = waste.
   * Range: 50–2000 mm.
   */
  defaultOffcutThresholdMm: z
    .number()
    .int('Must be a whole number')
    .min(50, 'Min 50mm')
    .max(2000, 'Max 2000mm')
    .optional(),

  /** Project-level unit preference (defaults to tenant setting). */
  defaultUnitCode: z.enum(UNIT_CODES).optional(),

  /** Free-text notes — maps to the ADR-007 base `remarks` column. Max 2000 chars. */
  remarks: z.string().max(2000, 'Remarks too long (max 2000)').optional(),
});

export type CreateProjectInput = z.infer<typeof CreateProjectSchema>;

/**
 * Body for `PATCH /api/v1/projects/:id`.
 *
 * All CreateProject fields become optional; at least one must be provided.
 */
export const UpdateProjectSchema = CreateProjectSchema.partial().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'At least one field must be provided' },
);

export type UpdateProjectInput = z.infer<typeof UpdateProjectSchema>;

/* ─── List query ─────────────────────────────────────────────────── */

/**
 * Query-string parameters for `GET /api/v1/projects`.
 *
 * `status` controls soft-delete visibility:
 *   - `active` (default) — is_deleted = 0 only
 *   - `deleted`          — is_deleted = 1 only
 *   - `all`              — no is_deleted filter
 */
export const ListProjectsQuerySchema = z.object({
  /** Cursor-based pagination: ID of the last item on the previous page. */
  cursor: z.coerce.number().int().positive().optional(),

  /** Page size. Default 50, max 100. */
  limit: z.coerce.number().int().min(1).max(100).default(50),

  /**
   * Visibility filter. Defaults to `active` (excludes soft-deleted rows).
   * Pass `deleted` for the restore workflow; `all` for admin views.
   */
  status: z.enum(['active', 'deleted', 'all']).default('active').optional(),

  /**
   * Full-text search: ILIKE against project `name` and `reference`.
   * Max 200 chars.
   */
  search: z.string().trim().max(200).optional(),
});

export type ListProjectsQuery = z.infer<typeof ListProjectsQuerySchema>;

/* ─── Share operations ───────────────────────────────────────────── */

/**
 * Body for `POST /api/v1/projects/:id/share/role`.
 *
 * Grants every user with `roleCode` in the current tenant read (and
 * potentially write, depending on role) access to the project.
 */
export const ShareProjectRoleSchema = z.object({
  roleCode: z.enum(SHAREABLE_ROLE_CODES),
});

export type ShareProjectRoleInput = z.infer<typeof ShareProjectRoleSchema>;

/**
 * Body for `POST /api/v1/projects/:id/share/user`.
 *
 * Grants a single specific user (within the same tenant) access to the project.
 */
export const ShareProjectUserSchema = z.object({
  userId: z.number().int().positive('User ID must be a positive integer'),
});

export type ShareProjectUserInput = z.infer<typeof ShareProjectUserSchema>;

/* ─── Response DTOs ──────────────────────────────────────────────── */

/** A single role-share row embedded in a project response. */
export const ProjectRoleShareDtoSchema = z.object({
  id: z.number().int(),
  roleCode: z.string(),
  grantedBy: z.number().int(),
  grantedAt: z.string().datetime(),
});

export type ProjectRoleShareDto = z.infer<typeof ProjectRoleShareDtoSchema>;

/** A single user-share row embedded in a project response. */
export const ProjectUserShareDtoSchema = z.object({
  id: z.number().int(),
  userId: z.number().int(),
  grantedBy: z.number().int(),
  grantedAt: z.string().datetime(),
});

export type ProjectUserShareDto = z.infer<typeof ProjectUserShareDtoSchema>;

/** Full project response shape — returned by detail and list endpoints. */
export const ProjectDtoSchema = z.object({
  id: z.number().int().positive(),
  tenantId: z.number().int().positive(),
  name: z.string(),
  reference: z.string().nullable(),
  clientName: z.string().nullable(),
  clientContact: z.string().nullable(),
  defaultKerfMm: z.number().int().nullable(),
  defaultStockBarMm: z.number().int().nullable(),
  defaultOffcutThresholdMm: z.number().int().nullable(),
  defaultUnitCode: z.string().nullable(),
  remarks: z.string().nullable(),
  isDeleted: z.union([z.literal(0), z.literal(1)]),
  createdBy: z.number().int(),
  createdDate: z.string().datetime(),
  updatedBy: z.number().int(),
  updatedDate: z.string().datetime(),
  deletedBy: z.number().int().nullable(),
  deletedDate: z.string().datetime().nullable(),
  /** Role-scoped shares on this project. */
  roleShares: z.array(ProjectRoleShareDtoSchema),
  /** User-scoped shares on this project. */
  userShares: z.array(ProjectUserShareDtoSchema),
});

export type ProjectDto = z.infer<typeof ProjectDtoSchema>;

/** Paginated list of projects — returned by `GET /api/v1/projects`. */
export const PaginatedProjectsDtoSchema = z.object({
  items: z.array(ProjectDtoSchema),
  nextCursor: z.number().int().nullable(),
  hasMore: z.boolean(),
  limit: z.number().int(),
});

export type PaginatedProjectsDto = z.infer<typeof PaginatedProjectsDtoSchema>;
